import { Menu, MenuItem, ListItemIcon, ListItemText, Avatar, Divider, IconButton } from '@mui/material';
import React from 'react';
import { useQueryClient } from 'react-query';
import { useMenu } from '../../../../../hooks/use-menu';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import { VyIcon } from '@vy/ui-design-system';
import { DOCS_URL } from '../vy-search-bar-consts';
import { VyAccountDropDownHeader } from './VyAccountDropDownHeader';
import type { Subsidiary } from '@vy/types';
import { SubsidiariesMenuItems } from './SubsidiariesMenuItems';
import { LocalStorageKeys } from '../../../../../constants/local-storage-keys';
import { withDefault } from '@vy/utils';
import { headerSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { useDescopeSdk } from '../../../../../hooks/use-descope-sdk.ts';
import { revokeServerSession } from '../../../../../services/user-auth';
import * as Sentry from '@sentry/react';

export type VyAccountDropDownProps = {
  accountName: string;
  subsidiaries: Subsidiary[];
  logo?: string;
};

export const VyAccountDropDown: React.FC<VyAccountDropDownProps> = ({ accountName, subsidiaries, logo }) => {
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();
  const navigate = useVyNavigate();
  const queryClient = useQueryClient();
  const descope = useDescopeSdk();

  const redirectToDocs = () => {
    window.open(DOCS_URL, '_blank');
  };

  const handleLogout = async () => {
    try {
      await revokeServerSession();
    } catch (error) {
      Sentry.captureException(error, { extra: { component: 'VyAccountDropDown', action: 'server-logout' } });
    }

    if (descope) {
      try {
        await descope.logout();
      } catch (error) {
        Sentry.captureException(error, { extra: { component: 'VyAccountDropDown', action: 'descope-logout' } });
      }
    }

    const isSideBarOpenKey = LocalStorageKeys.IsSideBarOpen;
    const sideBarOpenPersistedStateSnapshot = withDefault(localStorage.getItem(isSideBarOpenKey), 'true');

    localStorage.clear();
    localStorage.setItem(isSideBarOpenKey, sideBarOpenPersistedStateSnapshot);

    queryClient.clear();

    navigate('/login');
  };

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        {...getSelectorAttribute(headerSelectors.accountSettings.accountDropdownButton)}
        sx={{ p: 0 }}
      >
        <Avatar
          sx={{ width: 32, height: 32, bgcolor: '#E8EBF2', fontSize: '14px', color: '#000', fontWeight: 600 }}
        >
          {accountName.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorElement}
        open={isOpen}
        onClose={handleMenuClose}
        sx={{
          '& .MuiMenu-paper': {
            borderRadius: '8px',
            minWidth: '200px',
            boxShadow: '0px 4px 24px 0px rgba(16, 30, 54, 0.16)',
            p: 0,
          },
        }}
        slotProps={{
          list: { sx: { py: '1px' } }
        }}
      >
        <VyAccountDropDownHeader accountName={accountName} subsidiaries={subsidiaries} logo={logo} />

        <SubsidiariesMenuItems subsidiaries={subsidiaries} />

        <MenuItem
          onClick={redirectToDocs}
          {...getSelectorAttribute(headerSelectors.accountSettings.helpCenterButton)}
        >
          <ListItemIcon sx={{ minWidth: '24px !important' }}>
            <VyIcon icon="Docs" strokeOpacity={1} style={{ color: '#020F20' }} />
          </ListItemIcon>
          <ListItemText>Help center</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => navigate('/settings')}
          {...getSelectorAttribute(headerSelectors.accountSettings.settingsButton)}
        >
          <ListItemIcon sx={{ minWidth: '24px !important' }}>
            <VyIcon icon="Settings" strokeOpacity={1} style={{ color: '#020F20' }} />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ marginBottom: '8px' }}
          {...getSelectorAttribute(headerSelectors.accountSettings.logoutButton)}
        >
          <ListItemIcon sx={{ minWidth: '24px !important' }}>
            <VyIcon icon="Logout" color="#020F20" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
